import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface FeatureFlags {
  moqToAtom: boolean;
  authorize_unpacking: boolean;
  moqToDelivery: boolean;
  [key: string]: boolean;
}

const FeatureFlagContext = createContext<FeatureFlags | undefined>(undefined);

export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
};

interface FeatureFlagProviderProps {
  clientId?: string;
  children: ReactNode;
}

export const FeatureFlagProvider = ({
  clientId,
  children,
}: FeatureFlagProviderProps) => {

  const getDefaultFlags = (): FeatureFlags => ({
    moqToAtom: true,
    authorize_unpacking: false,
    env_prod: false,
    moqToDelivery: true,

  });

  const getInitialFlags = (): FeatureFlags => {
    if (!clientId) return getDefaultFlags();
    try {
      const stored = localStorage.getItem(`featureFlags_${clientId}`);
      return stored ? JSON.parse(stored) : getDefaultFlags();
    } catch (e) {
      console.warn("Failed to parse feature flags from localStorage:", e);
      return getDefaultFlags();
    }
  };

  const [flags, setFlags] = useState<FeatureFlags>(getInitialFlags);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    const fetchFeatureFlags = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_WEB_URL}/feature-flags/${clientId}/featureFlags.json?v=${new Date().getTime()}`);
        const data = await response.json();
        setFlags(data);
        localStorage.setItem(`featureFlags_${clientId}`, JSON.stringify(data));
      } catch (error) {
        console.error('Error fetching feature flags:', error);
        setFlags(getDefaultFlags());
      }
    };
    fetchFeatureFlags();
  }, [clientId]);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
};